/**
 * Progression detection for the three Boethian sequence types.
 *
 * Arithmetic: constant difference.       b - a = c - b
 * Geometric : constant ratio.            b/a = c/b   (equiv. b^2 = a*c, all > 0)
 * Harmonic  : reciprocals form AP.       b = 2ac/(a+c)  (equiv. 2ac = b*(a+c), all > 0)
 *
 * All checks require strictly increasing positive integers.
 */

export type ProgressionType = "arithmetic" | "geometric" | "harmonic";

export const ALL_PROGRESSIONS: readonly ProgressionType[] = ["arithmetic", "geometric", "harmonic"];

export function isArithmetic(a: number, b: number, c: number): boolean {
  return a < b && b < c && b - a === c - b;
}

export function isGeometric(a: number, b: number, c: number): boolean {
  return a > 0 && a < b && b < c && a * c === b * b;
}

export function isHarmonic(a: number, b: number, c: number): boolean {
  return a > 0 && a < b && b < c && 2 * a * c === b * (a + c);
}

/** Returns all progression types satisfied by the given three values. Order-insensitive. Requires distinct values. */
export function classifyProgression(values: readonly number[]): ProgressionType[] {
  if (values.length !== 3) return [];
  const sorted = [...values].sort((x, y) => x - y);
  const [a, b, c] = sorted as [number, number, number];
  if (a === b || b === c) return [];
  const types: ProgressionType[] = [];
  if (isArithmetic(a, b, c)) types.push("arithmetic");
  if (isGeometric(a, b, c)) types.push("geometric");
  if (isHarmonic(a, b, c)) types.push("harmonic");
  return types;
}

/** Generate all k-subsets of `arr` as arrays of T (values, not indices). */
export function combinations<T>(arr: readonly T[], k: number): T[][] {
  const out: T[][] = [];
  if (k < 0 || k > arr.length) return out;
  if (k === 0) return [[]];
  const buf: T[] = new Array(k);
  function helper(start: number, depth: number): void {
    if (depth === k) {
      out.push([...buf]);
      return;
    }
    const need = k - depth;
    for (let i = start; i <= arr.length - need; i++) {
      buf[depth] = arr[i];
      helper(i + 1, depth + 1);
    }
  }
  helper(0, 0);
  return out;
}

export interface ProgressionFind<T> {
  type: ProgressionType;
  members: T[]; // exactly 3
}

/** Find every 3-element progression among the values; reports which type(s) each triple satisfies. */
export function findAllTriples<T>(items: readonly T[], valueOf: (item: T) => number): ProgressionFind<T>[] {
  const out: ProgressionFind<T>[] = [];
  for (const triple of combinations(items, 3)) {
    const values = triple.map(valueOf);
    for (const t of classifyProgression(values)) {
      out.push({ type: t, members: triple });
    }
  }
  return out;
}

/**
 * Find a subset of 4 or more items whose value-triples cover ALL three progression types.
 * Returns the smallest such subset found, or null.
 */
export function findGloriousSet<T>(items: readonly T[], valueOf: (item: T) => number): T[] | null {
  for (let size = 4; size <= items.length; size++) {
    for (const subset of combinations(items, size)) {
      const found = new Set<ProgressionType>();
      for (const triple of combinations(subset, 3)) {
        for (const t of classifyProgression(triple.map(valueOf))) found.add(t);
        if (found.size === 3) break;
      }
      if (found.size === 3) return subset;
    }
  }
  return null;
}

/**
 * Find a subset of 4 or more items whose value-triples cover at least two distinct progression types.
 * Returns the smallest such subset found, or null. Note: a Glorious set is also a Royal set.
 */
export function findRoyalSet<T>(items: readonly T[], valueOf: (item: T) => number): T[] | null {
  for (let size = 4; size <= items.length; size++) {
    for (const subset of combinations(items, size)) {
      const found = new Set<ProgressionType>();
      for (const triple of combinations(subset, 3)) {
        for (const t of classifyProgression(triple.map(valueOf))) found.add(t);
        if (found.size >= 2) break;
      }
      if (found.size >= 2) return subset;
    }
  }
  return null;
}

/** Find any single 3-item progression among the items. Returns the first match (any type), or null. */
export function findGreaterTriple<T>(items: readonly T[], valueOf: (item: T) => number): ProgressionFind<T> | null {
  for (const triple of combinations(items, 3)) {
    const values = triple.map(valueOf);
    const types = classifyProgression(values);
    if (types.length > 0) return { type: types[0], members: triple };
  }
  return null;
}
